import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'tg-public-theme';

/**
 * Public-facing theme manager (light/dark) for landing + public pages.
 * Priority: visitor's saved choice (localStorage) > host default (server) > 'light'.
 *
 * @param {string|null} serverDefault - host-configured default theme ('light' | 'dark')
 */
export function usePublicTheme(serverDefault = null) {
  const [theme, setThemeState] = useState(() => {
    if (typeof window === 'undefined') return 'light';
    return window.localStorage.getItem(STORAGE_KEY) || 'light';
  });

  // Apply host default once it arrives, but only if visitor hasn't chosen.
  useEffect(() => {
    if (!serverDefault) return undefined;
    const saved = typeof window !== 'undefined' ? window.localStorage.getItem(STORAGE_KEY) : null;
    if (saved) return undefined;
    const t = setTimeout(() => setThemeState(serverDefault === 'dark' ? 'dark' : 'light'), 0);
    return () => clearTimeout(t);
  }, [serverDefault]);

  const setTheme = useCallback((next) => {
    setThemeState(next);
    try { window.localStorage.setItem(STORAGE_KEY, next); } catch (e) { /* noop */ }
  }, []);

  const toggle = useCallback(() => {
    setThemeState((cur) => {
      const next = cur === 'dark' ? 'light' : 'dark';
      try { window.localStorage.setItem(STORAGE_KEY, next); } catch (e) { /* noop */ }
      return next;
    });
  }, []);

  return { theme, setTheme, toggle };
}
